/**
 * Vendor-agnostic analytics facade (PIX-551).
 *
 * Every analytics call site in the app goes through this module; nothing in
 * product code imports the PostHog SDK. Three guarantees live here and must
 * not leak into callers:
 *
 *   1. Disabled by default. Until a client is attached, every method is a
 *      no-op - so local dev, where no key is configured, is silent. Same
 *      contract as Sentry's DSN gating.
 *   2. Never throws. Analytics is strictly subordinate to the product: a
 *      broken vendor, a blocked request, or a bug in here must never surface
 *      in a student's session.
 *   3. Super properties are merged on every event, with per-event properties
 *      winning on conflict.
 *
 * Deliberately SDK-free so it unit tests in plain node against a fake client
 * (tests/analytics.test.js). posthog.js is the only module that imports the
 * vendor SDK, and it attaches the real client here at init.
 */

/**
 * Build an analytics facade. Exported as a factory (rather than only the
 * singleton) so tests can drive it with a fake client instead of reaching for
 * a test-only mutator on module state.
 */
export function createAnalytics() {
  let client = null;
  let superProperties = {};
  // Who this facade last identified, or null. Tracked here rather than asked
  // of the vendor because the vendor's distinct id is also its anonymous id,
  // and "an anonymous browser" and "a known person" need different treatment
  // when someone else identifies - see identitySwitch.js.
  let identifiedId = null;
  const eventSubscribers = new Set();

  /**
   * Run a client call, swallowing anything it throws. Returns the callback's
   * value, or `fallback` when disabled or on failure.
   */
  const safely = (fn, fallback = undefined) => {
    if (!client) return fallback;
    try {
      return fn(client);
    } catch (_) {
      return fallback;
    }
  };

  return {
    /** Attach the vendor client. Passing null/undefined disables capture. */
    attach(nextClient) {
      client = nextClient ?? null;
    },

    /** True once a client is attached - i.e. a project key was configured. */
    isEnabled() {
      return client !== null || eventSubscribers.size > 0;
    },

    /**
     * Merge properties into the set attached to every subsequent event.
     * Registered with the vendor too, so events it emits on its own behalf
     * (page leave, heatmaps) carry the same context.
     */
    registerSuperProperties(properties) {
      if (!properties) return;
      superProperties = { ...superProperties, ...properties };
      safely((c) => c.register(superProperties));
    },

    /** Read the current super-property bag (used by tests and diagnostics). */
    getSuperProperties() {
      return { ...superProperties };
    },

    /** Emit an event. Names must come from the taxonomy in events.js. */
    track(event, properties) {
      if (!event) return;
      const mergedProperties = { ...superProperties, ...properties };
      safely((c) => c.capture(event, mergedProperties));
      eventSubscribers.forEach((subscriber) => {
        try {
          subscriber(event, mergedProperties);
        } catch (_) {
          // One broken analytics destination must never block another vendor
          // or, more importantly, the student action being measured.
        }
      });
    },

    /**
     * Observe normalised app events without pretending every vendor has a
     * PostHog-shaped SDK. Used by narrow destinations such as Meta Pixel.
     */
    subscribe(subscriber) {
      if (typeof subscriber !== 'function') return () => {};
      eventSubscribers.add(subscriber);
      return () => eventSubscribers.delete(subscriber);
    },

    /** Associate subsequent activity with a user, by internal id only. */
    identify(distinctId, properties) {
      if (!distinctId) return;
      // Recorded even when disabled, so the identity-switch rule behaves the
      // same in local dev as in production rather than only being exercised
      // where it cannot be observed.
      identifiedId = String(distinctId);
      safely((c) => c.identify(String(distinctId), properties));
    },

    /**
     * Who this facade last identified, or null if nobody has - which includes
     * after a reset. Consumed by the identity-switch rule to tell "an
     * anonymous browser" from "a different known person".
     */
    identifiedDistinctId() {
      return identifiedId;
    },

    /**
     * Clear identity and accumulated context. Super properties are dropped
     * even when disabled, so a logout during a no-key session cannot leave
     * one user's context attached to the next.
     */
    reset() {
      superProperties = {};
      identifiedId = null;
      safely((c) => c.reset());
    },

    /** Associate activity with a group (per-school rollups). */
    associateGroup(groupType, groupKey, properties) {
      if (!groupType || !groupKey) return;
      safely((c) => c.group(groupType, String(groupKey), properties));
    },

    /**
     * The vendor's current distinct id. Needed by the anonymous-to-registered
     * bridge (PIX-554), which hands it to the backend so a landing visit and
     * the account it becomes resolve to one person. Null when disabled.
     */
    getDistinctId() {
      return safely((c) => c.get_distinct_id(), null) ?? null;
    },
  };
}

/** App-wide singleton. Call sites import this; posthog.js attaches to it. */
export const analytics = createAnalytics();
